import { useGetAllDiscountListQuery } from '@api/pos/pos-bo-discount-list.api.rtk';
import { useGetTableDetailsQuery } from '@api/pos/pos-bo-table-management.api.rtk';
import {
  useDeleteVoidOrdersMutation,
  useGenerateOrderSlip,
  useGetOrders,
  usePostDiscount,
  usePostOrder,
} from '@api/pos/pos-transaction-api.rtk';
import LoadingComponent from '@components/animation/loading-component';
import useAppContext from '@hooks/useAppContext';
import { useBlockNavigation } from '@hooks/useBlockNavigation';
import { ConfirmationDialog } from '@hooks/useConfirmationDialog';
import useCreateContext from '@hooks/useCreateContext';
import useDisclosure, { UseDisclosureInterface } from '@hooks/useDisclosure';
import { ModalWrapper } from '@hooks/useModal';
import useToast from '@hooks/useToast';
import useUser from '@hooks/useUser';
import FORoomPostingDiscountForm from '@modules/front-office/components/room-posting/drawer form/fo-roomposting-discount-form';
import {
  IDiscountRegular,
  IPosOrder,
  TPosDiscountRegular,
} from '@modules/point-of-sale/interface/orders/pos-orders.interface';
import {
  selectPOSisHaveOrders,
  selectPOSstationID,
  selectPOSTableAreas,
  selectPOSTableDetails,
  selectPOSTableOrders,
  selectPOSTableTransactionID,
  selectPOSTableTransactions,
} from '@modules/point-of-sale/slice/pos-selector';
import {
  clearPOSTableArea,
  clearTableOrder,
  isSamePOSOrderLine,
  setTransactionDetails,
} from '@modules/point-of-sale/slice/pos-table-area.slice';
import {
  Add,
  Cancel,
  CancelOutlined,
  Close,
  DeleteSweep,
  SubdirectoryArrowRightOutlined,
  TableRestaurant,
} from '@mui/icons-material';
import { LoadingButton, TabContext, TabList } from '@mui/lab';
import {
  Button,
  Card,
  Collapse,
  debounce,
  Dialog,
  Divider,
  IconButton,
  Stack,
  Tab,
  TextField,
  Typography,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '@store/index';
import { selectPropertyID } from '@store/selector/property.selector';
import { parseNumber, payloadDateFormat } from '@utils/format';
import { buildPosOrderPayload } from '@utils/pos-utils';
import { usePOSScreen } from '@utils/screen-size';
import { hashSignature } from '@utils/security';
import moment from 'moment';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getColor } from '../../../../../assets/theme';
import POSConfirmationDialog from '../../pos-selector/pos-confirmation-dialog';
import POSActionButtons from './pos-action-buttons';
import POSDiscountSection from './pos-discount-section';
import POSOrderHeader from './pos-order-header';
import POSOrderItem from './pos-order-item';
import POSOthersDrawer from './table-drawer/pos-others.drawer';
import POSSelectTableAreaDrawer from './table-drawer/pos-select-table-area.drawer';
import POSTableDetailsDrawer from './table-drawer/pos-table-details.drawer';

/**
 * Props for the POSTableDrawer context.
 *
 * @typedef {Object} IPOSTableDrawer
 * @property {UseDisclosureInterface} discountModalDisclosure - Disclosure state for the discount modal.
 * @property {UseDisclosureInterface} tableDrawerDisclosure - Disclosure state for the table selection drawer.
 * @property {UseDisclosureInterface} tableDetailsTaxPayerDisclosure - Disclosure state for the table details drawer.
 * @property {UseDisclosureInterface} tableDetailsDrawerDisclosure - Disclosure state for the table tax payer details drawer.
 */

interface IPOSTableDrawer {
  discountModalDisclosure: UseDisclosureInterface;
  tableDrawerDisclosure: UseDisclosureInterface;
  tableDetailsDrawerDisclosure: UseDisclosureInterface;
  tableDetailsTaxPayerDisclosure: UseDisclosureInterface;
  othersDrawerDisclosure: UseDisclosureInterface;
  tableTransferDisclosure: UseDisclosureInterface;
  loadingWindow: UseDisclosureInterface;
  autoSelectTableState: [string, React.Dispatch<React.SetStateAction<string>>];
  selectedItems: IPosOrder[];
}

interface IPOSOrderDetails {
  isBillOut?: boolean;
}

const { CtxProvider, useCtx } =
  useCreateContext<IPOSTableDrawer>('POSTableDrawerCtx');

const { CtxProvider: OrderCtxProvider, useCtx: useOrderCtx } =
  useCreateContext<IPOSOrderDetails>('POSOrderDetailsCtx');

/**
 * Custom hook to use the POS Window Drawer context.
 *
 * @returns {ReturnType<typeof useCtx>} The context value.
 */
export const usePOSWindowDrawerCtx = () => useCtx();
export const usePOSOrderDetailsCtx = () => useOrderCtx();

/**
 * POSOrderTakingWindow component handles the order-taking window in the POS system.
 * It allows users to manage orders, including adding, removing, and voiding items.
 *
 * @returns {JSX.Element} The rendered component.
 */
const POSOrderTakingWindow = ({
  isBillOut,
  disabledLoading,
}: {
  isBillOut?: boolean;
  disabledLoading?: boolean;
}) => {
  // #region STATE
  const { white, lightGray, primary, discountBG, textSecondary } = getColor();
  const { openSecCodeDialog, startLoadingStatus, stopLoadingStatus } =
    useAppContext();
  const dispatch = useAppDispatch();
  const { toast } = useToast();

  const selectedTableTransactionID = useAppSelector(
    selectPOSTableTransactionID,
  );
  const transactionDetails = useAppSelector(selectPOSTableDetails);
  const tableDetails = useAppSelector(selectPOSTableDetails);
  const transactionID = useAppSelector(selectPOSTableTransactionID);
  const orders = useAppSelector(selectPOSTableOrders);
  const areas = useAppSelector(selectPOSTableAreas);
  const isHaveOrders = useAppSelector(selectPOSisHaveOrders);
  const tableTransactions = useAppSelector(selectPOSTableTransactions);
  const propertyID = useAppSelector(selectPropertyID);
  const stationID = useAppSelector(selectPOSstationID);
  const autoSelectTableState = useState('');
  const isPOS1024p = usePOSScreen();

  const location = useLocation();
  const dialogDisclosure = useDisclosure();

  const shouldBlock =
    isHaveOrders && location.pathname === '/point-of-sale/dashboard';

  const blocker = useBlockNavigation(shouldBlock);

  useEffect(() => {
    if (blocker.status !== 'blocked') return;
    dialogDisclosure.onOpen();
  }, [blocker]);
  // #endregion

  // #region API
  const { isLoading, isFetching, refetch } = useGetOrders(transactionID, {
    skip: !transactionID,
    refetchOnMountOrArgChange: true,
  });

  const {
    isLoading: isLoadingTableDetails,
    isFetching: isFetchingTableDetails,
  } = useGetTableDetailsQuery(transactionID, {
    skip: !transactionID,
    refetchOnMountOrArgChange: true,
  });

  // #endregion

  // #region BILLOUT
  const [billOutStatus, setBillOutStatus] = useState<string>();
  const [generateOrderSlip] = useGenerateOrderSlip();
  const hasGeneratedOS = useRef(false);

  useEffect(() => {
    if (!isBillOut) {
      hasGeneratedOS.current = false;
      return stopLoadingStatus();
    }
    setBillOutStatus('Processing');

    if (isLoading || isFetching) {
      startLoadingStatus('Loading Orders');
      return;
    }

    if (isLoadingTableDetails || isFetchingTableDetails) {
      startLoadingStatus('Loading Table Details');
      return;
    }

    const notPrinted = orders?.filter((o) => !o.isPrintedOS) ?? [];
    if (notPrinted.length === 0 || hasGeneratedOS.current) {
      stopLoadingStatus();
      return;
    }

    hasGeneratedOS.current = true;

    (async () => {
      // startLoadingStatus('Generating Order Slip');

      try {
        await generateOrderSlip({
          transactionID,
          payload: {
            properties: [
              {
                property: {
                  propertyID,
                  signature: hashSignature(String(propertyID)),
                },
              },
            ],
          },
        }).unwrap();
      } catch (err) {
        toast({ severity: 'warning', message: err });
      } finally {
        stopLoadingStatus();
        setBillOutStatus(undefined);
      }
    })();
  }, [
    isBillOut,
    isLoading,
    isFetching,
    isLoadingTableDetails,
    isFetchingTableDetails,
    orders,
  ]);
  // #endregion

  // #region REMOVE / VOID ITEMS
  const [voidOrders, { isLoading: isVoiding }] = useDeleteVoidOrdersMutation();
  const [isOpenVoidDialog, setIsOpenVoidDialog] = useState(false);
  const [isOpenRemarks, setIsOpenRemarks] = useState(false);
  const [voidRemarks, setVoidRemarks] = useState('');
  const [approvedBy, setApprovedBy] = useState('');
  const user = useUser();

  const debouncedChange = useCallback(
    debounce((newValue: string) => {
      setVoidRemarks?.(newValue);
    }, 20),
    [],
  );

  // State for editing items
  const editItemsState = useState<IPosOrder[]>([]);
  const [editItems, setEditItems] = editItemsState;
  const [clearAll, setClearAll] = useState(false);

  // Check if the item is voided
  const isVoid = useMemo(
    () => editItems?.some((item) => item.isPrintedOS) ?? false,
    [editItems],
  );

  // REMOVE
  const handleRemoveItem = async (forceClearAll: boolean) => {
    if (orders?.length <= 0) return;
    try {
      const isClearAll = clearAll === true || forceClearAll === true;
      const targetOrders = isClearAll ? orders : editItems;

      if (!targetOrders?.length && !isClearAll) {
        toast({
          message: 'No item selected for removal',
          severity: 'warning',
        });
        return;
      }

      startLoadingStatus('Clearing Item(s)');

      const hasOrderId = targetOrders.some((item) => item.orderID);

      if (hasOrderId) {
        const voidableOrders = targetOrders.filter(
          (item) => !!item.orderID && !item.isPrintedOS,
        );

        if (voidableOrders.length > 0) {
          const signature = hashSignature(
            [propertyID, stationID, voidableOrders.length, ' ', user.name]
              .map((v) => String(v ?? ''))
              .join(''),
          );

          const payload = {
            transactionID,
            payload: {
              properties: [
                {
                  property: {
                    propertyID,
                    voidOrder: {
                      stationID,
                      orderRefIDs: voidableOrders.map((item) => item.orderID),
                      reason: ' ',
                      approvedBy: user.name,
                      signature,
                    },
                  },
                },
              ],
            },
          };

          await voidOrders(payload).unwrap();
        }
      }

      if (isClearAll) {
        dispatch(clearTableOrder());
        setEditItems([]);
      } else {
        await Promise.all(
          editItems.map((element) =>
            dispatch(
              clearTableOrder({
                menuID: element.menuID,
                orderID: element.orderID,
                unitPrice: element.unitPrice,
                lineRemarks: element.lineRemarks,
              }),
            ),
          ),
        );

        setEditItems((prev) =>
          prev.filter(
            (prevItem) =>
              !editItems.some((e) => isSamePOSOrderLine(e, prevItem)),
          ),
        );
      }

      toast({
        message: 'Item(s) removed successfully',
        severity: 'success',
      });
      setClearAll(false);
    } catch (error) {
      toast({ severity: 'warning', message: error });
    } finally {
      stopLoadingStatus();
    }
  };

  // VOID
  // PROCESS 1
  const handleOpenVoidWarning = () => {
    // Open void warning dialog if there are items to void
    if (orders?.length <= 0) return setIsOpenVoidDialog(false);

    setIsOpenVoidDialog(true);
  };

  const handleCloseVoidWarning = () => {
    setIsOpenVoidDialog(false);
    setIsOpenRemarks(false);
    setClearAll(false);
  };

  // PROCESS 2
  const handleOpenRemarks = async () => {
    setIsOpenVoidDialog(false);
    openSecCodeDialog(async (res) => {
      if (!res?.data.isValid) return;
      setIsOpenRemarks(true);
      setApprovedBy(res?.data?.user || user?.name || 'No User Found');
    }, 'void_sales');
  };

  const handleCloseRemarks = () => {
    setVoidRemarks('');
    setIsOpenRemarks(false);
    setClearAll(false);
  };

  // PROCESS 3
  const handleOpenRemarksDialog = async () => {
    // setIsOpenRemarks(false);
    // setIsOpenRemarksDialog(true);
    await handleVoid();
  };

  // VOID FUNC
  // const handleWaste = () => {
  //   if (clearAll) {
  //     setEditItems([]);
  //     toast({ message: 'Items successfully wasted!', severity: 'success' });
  //     setClearAll(false);
  //     setVoidRemarks('');
  //     return setIsOpenRemarksDialog(false);
  //   }
  //   toast({ message: 'Items successfully wasted!', severity: 'success' });
  //   setEditItems([]);
  //   setVoidRemarks('');
  //   setIsOpenRemarksDialog(false);
  // };

  const handleVoid = async () => {
    try {
      if (!propertyID || !stationID) {
        toast({
          message: 'Missing property or station information.',
          severity: 'error',
        });
        return;
      }

      const isClearAll = clearAll === true;
      const targetOrders = isClearAll ? orders : editItems;

      if (!targetOrders?.length) {
        toast({
          message: 'No items selected for voiding.',
          severity: 'warning',
        });
        return;
      }

      const signature = hashSignature(
        [propertyID, stationID, targetOrders.length, voidRemarks, approvedBy]
          .map((v) => String(v ?? ''))
          .join(''),
      );

      const payload = {
        transactionID,
        payload: {
          properties: [
            {
              property: {
                propertyID,
                voidOrder: {
                  stationID,
                  orderRefIDs: targetOrders.map((item) => item.orderID),
                  reason: voidRemarks,
                  approvedBy,
                  signature,
                },
              },
            },
          ],
        },
      };
      const { results } = await voidOrders(payload).unwrap();

      if (results?.responseCode === 200) {
        toast({
          message: 'Item(s) voided successfully',
          severity: 'success',
        });
      } else {
        toast({
          message: `${results.responseMessage || 'Failed to Void Items'}`,
          severity: 'error',
        });
      }
    } catch (error) {
      toast({
        message: error || 'Failed to void items. Please try again.',
        severity: 'error',
      });
    } finally {
      // Reset states
      setEditItems([]);
      setClearAll(false);
      setVoidRemarks('');
      setIsOpenRemarks(false);
      stopLoadingStatus();
    }
  };

  // Reset state after table change
  useEffect(() => {
    setEditItems([]);
  }, [selectedTableTransactionID]);

  // #endregion

  // #region SPLIT BILL
  const [selectedTransaction, setSelectedTransaction] = useState(0);
  const [initialSelectedTransaction, setInitialSelectedTransaction] =
    useState<number>();

  const handleChange = (_, newValue: number) => {
    const isHaveNewItems = orders?.some(
      (item) => !item.isPrintedOS && !item.orderID,
    );
    setEditItems([]);
    if (isHaveNewItems) {
      setInitialSelectedTransaction(newValue);
      dialogDisclosure.onOpen();
      return;
    }
    setSelectedTransaction(newValue);
    const selectedTransaction = tableTransactions?.[newValue];
    selectedTransaction && dispatch(setTransactionDetails(selectedTransaction));
  };

  useEffect(() => {
    setSelectedTransaction(0);
  }, [tableTransactions]);
  // #endregion

  // #region TABLE DRAWER / DISCOUNT MODAL
  const tableDrawerDisclosure = useDisclosure();
  const tableDetailsDrawerDisclosure = useDisclosure();
  const tableDetailsTaxPayerDisclosure = useDisclosure();
  const discountModalDisclosure = useDisclosure();
  const othersDrawerDisclosure = useDisclosure();
  const tableTransferDisclosure = useDisclosure();
  const loadingWindow = useDisclosure();
  // #endregion

  // #region DISCOUNT

  const { data: discounts, discountList } = useGetAllDiscountListQuery(
    // stationID,
    {
      // discountID: selectedData!,
      filter: {
        isActive: '1',
      },
    },
    {
      selectFromResult: ({ data, ...item }) => ({
        ...item,
        data: data?.results,
        discountList: data?.results?.map((item) => item.discountAcronym) ?? [],
      }),
    },
  );

  const [postOrder] = usePostOrder();
  const [postDiscount] = usePostDiscount();

  const totalAmount = useMemo(() => {
    const source = editItems.length > 0 ? editItems : orders;
    return source.reduce(
      (prev, cur) => prev + parseNumber(cur.grossAmount?.toString()),
      0,
    );
  }, [orders, editItems]);

  // #endregion

  // Memoize context values to avoid unnecessary re-renders
  const memoizedValue = useMemo(
    () => ({
      tableDrawerDisclosure,
      tableDetailsDrawerDisclosure,
      tableDetailsTaxPayerDisclosure,
      discountModalDisclosure,
      othersDrawerDisclosure,
      tableTransferDisclosure,
      autoSelectTableState,
      loadingWindow,
      selectedItems: editItems,
    }),
    [
      tableDrawerDisclosure.isOpen,
      tableDetailsDrawerDisclosure.isOpen,
      tableDetailsTaxPayerDisclosure.isOpen,
      discountModalDisclosure.isOpen,
      othersDrawerDisclosure.isOpen,
      tableTransferDisclosure.isOpen,
      tableDetails,
      autoSelectTableState[0],
      loadingWindow.isOpen,
      editItems,
    ],
  );

  return (
    <>
      {/* MAIN WINDOW */}
      <OrderCtxProvider value={{ isBillOut }}>
        <Stack
          sx={{
            ...(!isBillOut && { p: 1, px: { xs: 0, sm: 1 } }),
            position: 'sticky !important',
            top: '0px',
            height: '100%',
          }}
        >
          <Card
            sx={{
              position: 'relative',
              display: 'flex',
              bgcolor: white,
              borderRadius: '12px',
              justifyContent: 'center',
              boxShadow: 0,
              flex: 1,
              minHeight: 0,
            }}
          >
            {(isLoading ||
              isFetching ||
              loadingWindow.isOpen ||
              isLoadingTableDetails ||
              isFetchingTableDetails) &&
              !disabledLoading &&
              !billOutStatus && <LoadingComponent />}
            {selectedTableTransactionID ? (
              <Stack
                sx={{
                  height: '100%',
                  width: '100%',
                  alignContent: 'center',
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <POSOrderHeader
                  {...{
                    tableDrawerDisclosure,
                    tableDetailsDrawerDisclosure,
                    loadingWindow,
                  }}
                />
                {/* SPLIT BILL */}
                {tableTransactions && tableTransactions?.length > 1 && (
                  <TabContext value={selectedTransaction}>
                    <Divider />
                    <TabList
                      onChange={handleChange}
                      variant='scrollable'
                      scrollButtons='auto'
                    >
                      {tableTransactions?.map((_, i) => {
                        return (
                          <Tab
                            key={i}
                            value={i}
                            label={i === 0 ? 'Main Table' : 'Sub Table ' + i}
                            icon={
                              i === 0 ? (
                                <TableRestaurant />
                              ) : (
                                <SubdirectoryArrowRightOutlined />
                              )
                            }
                            iconPosition='start'
                            disableRipple
                            sx={{
                              minHeight: '45px',
                              textTransform: 'capitalize',
                            }}
                            disabled={isBillOut}
                          />
                        );
                      })}
                    </TabList>
                  </TabContext>
                )}
                {/* END SPLIT BILL */}
                <Divider />
                <Collapse in={editItems?.length > 0}>
                  <Stack
                    sx={{
                      px: 2,
                      py: 1,
                      gap: 2,
                      justifyContent: 'space-between',
                    }}
                    direction='row'
                  >
                    <Stack direction='row' gap={2} alignItems='center'>
                      <IconButton
                        size={isPOS1024p ? 'small' : 'medium'}
                        sx={{
                          bgcolor: lightGray,
                          ':hover': {
                            bgcolor: primary,
                            color: '#FFF',
                          },
                        }}
                        onClick={() => setEditItems([])}
                      >
                        <Close sx={isPOS1024p ? { fontSize: '16px' } : {}} />
                      </IconButton>
                      <Typography
                        variant='h6'
                        sx={{ fontSize: '20px', fontWeight: 600 }}
                      >
                        {editItems?.length !== 0 && editItems?.length}
                      </Typography>
                    </Stack>
                    <Stack direction='row' gap={1}>
                      <Button
                        variant='contained'
                        size={isPOS1024p ? 'small' : 'large'}
                        color='error'
                        startIcon={isVoid ? <Cancel /> : <DeleteSweep />}
                        onClick={
                          isVoid
                            ? handleOpenVoidWarning
                            : () => handleRemoveItem(false)
                        }
                      >
                        {isVoid ? 'Void' : 'Clear'}
                      </Button>
                    </Stack>
                  </Stack>
                </Collapse>
                <Stack
                  sx={{
                    overflowY: 'auto',
                    mb: 1,
                    flex: 1,
                    minHeight: 0,
                    ...(isBillOut && {
                      minWidth: '400px',
                    }),
                  }}
                >
                  {orders?.map((item, i) => (
                    <POSOrderItem
                      key={item.menuID + i}
                      {...{
                        item,
                        editItemsState,
                        openRemoveWarning: () => handleRemoveItem(false),
                      }}
                    />
                  ))}
                </Stack>
                <Stack gap={2}>
                  <POSDiscountSection
                    {...{
                      openRemoveWarning: () => {
                        setClearAll(true);
                        handleRemoveItem(true);
                      },
                      openVoidWarning: () => {
                        setClearAll(true);
                        handleOpenVoidWarning();
                      },
                      netPayable: 0,
                      discount: 0,
                      editItemsState,
                    }}
                  />
                  <CtxProvider value={memoizedValue}>
                    <POSActionButtons />
                  </CtxProvider>
                </Stack>
              </Stack>
            ) : (
              <Stack
                sx={{
                  height: '100%',
                  alignContent: 'center',
                  justifyContent: 'center',
                }}
              >
                <Button
                  size='large'
                  color='primary'
                  variant='contained'
                  startIcon={areas?.length <= 0 ? undefined : <Add />}
                  onClick={tableDrawerDisclosure.onOpen}
                  disabled={areas?.length <= 0}
                >
                  {areas?.length <= 0 ? 'No Table Found' : 'Table Select'}
                </Button>
              </Stack>
            )}
          </Card>
        </Stack>
      </OrderCtxProvider>

      {/* DRAWER , DIALOG */}
      <>
        {/* VOID DIALOG */}
        <ConfirmationDialog
          dialogStatus='warning'
          iconColor='warning'
          open={isOpenVoidDialog}
          title={`Void Item${editItems?.length > 1 ? 's' : ''}`}
          desc={
            <Stack>
              <Typography sx={{ fontSize: '16px' }}>
                Are you sure you want to void the selected item(s)?
              </Typography>
              <Typography sx={{ fontSize: '16px' }}>
                This action is permanent and cannot be undone.
              </Typography>
            </Stack>
          }
          onConfirm={handleOpenRemarks}
          onClose={handleCloseVoidWarning}
          cancelButtonTitle='No'
          confirmButtonTitle='Yes'
        />
        {/* REMARKS DIALOG */}
        <Dialog open={isOpenRemarks}>
          <Stack sx={{ p: 3, gap: 1, width: '420px' }}>
            {/* HEADER */}
            <Stack alignItems='flex-start'>
              <Stack
                sx={{ bgcolor: discountBG, p: '12px', borderRadius: '100px' }}
              >
                <CancelOutlined sx={{ color: primary, fontSize: '35px' }} />
              </Stack>
              <Typography variant='h5' sx={{ fontWeight: 600, py: 2 }}>
                Void Remarks
              </Typography>
            </Stack>
            {/* CONTENT */}
            <Stack gap={2} pb={3}>
              <Typography sx={{ color: textSecondary, fontSize: '16px' }}>
                Provide a reason for voiding the item.
              </Typography>
              <TextField
                multiline
                rows={5}
                label='Remarks'
                InputLabelProps={{
                  shrink: true,
                }}
                value={voidRemarks}
                onChange={(e) => debouncedChange(e.target.value)}
              />
            </Stack>
            {/* BUTTON */}
            <Stack direction='row' gap={1} ml='auto'>
              <Button
                variant='outlined'
                size='large'
                onClick={handleCloseRemarks}
                disabled={isVoiding}
              >
                Close
              </Button>
              <LoadingButton
                variant='contained'
                size='large'
                disabled={!voidRemarks}
                onClick={handleOpenRemarksDialog}
                loading={isVoiding}
              >
                Submit
              </LoadingButton>
            </Stack>
          </Stack>
        </Dialog>
        {/* <ConfirmationDialog
          dialogStatus='warning'
          iconColor='warning'
          open={isOpenRemarksDialog}
          title='Void Items'
          desc={
            <Stack gap={2} sx={{ maxWidth: '350px' }}>
              <Typography>
                The item(s) you voided contains one or more stock items. Select
                WASTE to deduct from the inventory otherwise click SAVE.
              </Typography>
              <Alert severity='error' sx={{ color: danger }}>
                The action you're about to do cannot be undone. Should you need
                to offset the quantity, please do so using the Back Office.
              </Alert>
            </Stack>
          }
          onConfirm={async()=>{}}
          confirmButtonTitle='Save'
          // backBtn={
          //   <Button variant='contained' color='error' onClick={handleWaste}>
          //     Waste
          //   </Button>
          // }
          backBtn={
            <Button
              variant='outlined'
              onClick={() => {
                setIsOpenRemarksDialog(false);
                setVoidRemarks('');
              }}
            >
              Cancel
            </Button>
          }
        /> */}
        {/* DRAWER */}
        <CtxProvider value={memoizedValue}>
          <POSSelectTableAreaDrawer />
          <POSTableDetailsDrawer />
          <POSOthersDrawer />
        </CtxProvider>

        {useMemo(
          () => (
            <ModalWrapper
              disableOnClose
              boxStyle={{
                width: {
                  xs: '100%',
                  md: '830px',
                },
              }}
              modalDisclosure={discountModalDisclosure}
            >
              <FORoomPostingDiscountForm
                fromModule='Rates Plotting'
                handleClose={discountModalDisclosure.onClose}
                applyBtnTypeSubmit={false}
                onConfirm={async (data) => {
                  await openSecCodeDialog(
                    async (res) => {
                      try {
                        if (!res?.data.isValid) return;

                        const {
                          athlete_id: athleteID,
                          senior_id: seniorID,
                          pwd_id: pwdID,
                          solo_parent_id: soloParentID,
                        } = data;

                        const athleteIdList = athleteID?.id_list ?? [];
                        const seniorIdList = seniorID?.id_list ?? [];
                        const pwdIdList = pwdID?.id_list ?? [];
                        const soloParentIdList = soloParentID?.id_list ?? [];

                        // Extracted helper to reduce repetition
                        const createMissingOrders = async (
                          items: typeof editItems,
                        ): Promise<typeof editItems> => {
                          const noOrderID =
                            items?.filter((item) => !item.orderID) ?? [];
                          if (noOrderID.length === 0) return items;

                          const results: { menuID: string; orderID: string }[] =
                            await postOrder({
                              transactionID,
                              payload: {
                                properties: [
                                  {
                                    property: {
                                      propertyID,
                                      posOrders: buildPosOrderPayload({
                                        orders: noOrderID,
                                        propertyID,
                                        stationID,
                                      }),
                                    },
                                  },
                                ],
                              },
                              noRefetch: true,
                            }).unwrap();

                          dispatch(
                            clearTableOrder({
                              clearNoID: noOrderID.map((item) => item.menuID),
                            }),
                          );

                          // merge orderIDs
                          return items.map((item) => {
                            if (item.orderID) return item;
                            const match = results.find(
                              (r) => r.menuID === item.menuID,
                            );
                            return match
                              ? { ...item, orderID: match.orderID }
                              : item;
                          });
                        };

                        const amount = Number(data.manual_discount ?? 0);

                        // Handle Regular Discount
                        if (data.regularDiscount) {
                          const discountType = 'Regular' as const;

                          const selectedDiscount = discounts?.find(
                            (item) =>
                              item.discountAcronym === data.customDiscount,
                          );

                          const discountID = selectedDiscount?.referenceID;

                          if (!discountID) {
                            toast({
                              severity: 'warning',
                              message: 'No Discount ID found.',
                            });
                            return;
                          }

                          const updatedEditItems =
                            await createMissingOrders(editItems);

                          const discountPayload: TPosDiscountRegular = {
                            properties: [
                              {
                                property: {
                                  propertyID,
                                  isGroupComputation: false,
                                  applyDiscounts: updatedEditItems.map(
                                    (item) => ({
                                      applyDiscount: {
                                        orderID: item.orderID,
                                        discountID,
                                        discountType,
                                        amount,
                                        signature: hashSignature(
                                          `${propertyID}${item.orderID}${discountID}${discountType}0${amount}`,
                                        ),
                                      },
                                    }),
                                  ),
                                  signature: hashSignature(
                                    `${propertyID}false${updatedEditItems.length}`,
                                  ),
                                },
                              },
                            ],
                          };

                          await postDiscount({
                            transactionID,
                            payload: discountPayload,
                          })
                            .unwrap()
                            .then(() => {
                              toast({
                                severity: 'success',
                                message: 'Discount applied successfully',
                              });
                              discountModalDisclosure.onClose();
                              setEditItems([]);
                            })
                            .catch((error) => {
                              toast({ severity: 'warning', message: error });
                              setEditItems([]);
                              discountModalDisclosure.onClose();
                            });
                          return;
                        }

                        // Handle Athlete / Senior / PWD Discounts
                        if (
                          (athleteIdList.length ?? 0) > 0 ||
                          (seniorIdList.length ?? 0) > 0 ||
                          (pwdIdList.length ?? 0) > 0 ||
                          (soloParentIdList.length ?? 0) > 0
                        ) {
                          const isGroupComputation = editItems.length <= 0;

                          const updatedEditItems = await createMissingOrders(
                            isGroupComputation
                              ? (orders?.filter(
                                  (item) => item.discountTotal <= 0,
                                ) ?? [])
                              : editItems,
                          );

                          const applyDiscounts: {
                            applyDiscount: IDiscountRegular;
                          }[] = isGroupComputation
                            ? [
                                ...(seniorIdList?.map((senior) => ({
                                  applyDiscount: {
                                    orderID: null,
                                    discountID: null,
                                    discountType: 'Senior Citizen' as const,
                                    idNo: senior.id,
                                    additionalDetails: {
                                      name: senior.name,
                                      tin: senior?.tin ?? '',
                                    },
                                    amount,
                                    signature: hashSignature(
                                      `${propertyID}nullnullSenior Citizen${senior.id}${isGroupComputation ? 1 : 0}${senior.name}${senior?.tin ?? ''}${amount}`,
                                    ),
                                  },
                                })) ?? []),

                                ...(pwdIdList?.map((pwd) => ({
                                  applyDiscount: {
                                    orderID: null,
                                    discountID: null,
                                    discountType: 'PWD' as const,
                                    idNo: pwd.id,
                                    additionalDetails: {
                                      name: pwd.name,
                                      tin: pwd?.tin ?? '',
                                    },
                                    amount,
                                    signature: hashSignature(
                                      `${propertyID}nullnullPWD${pwd.id}${isGroupComputation ? 1 : 0}${pwd.name}${pwd?.tin ?? ''}${amount}`,
                                    ),
                                  },
                                })) ?? []),

                                ...(athleteIdList?.map((athlete) => ({
                                  applyDiscount: {
                                    orderID: null,
                                    discountID: null,
                                    discountType: 'Athlete' as const,
                                    idNo: athlete.id,
                                    additionalDetails: {
                                      name: athlete.name,
                                    },
                                    amount,
                                    signature: hashSignature(
                                      `${propertyID}nullnullAthlete${athlete.id}${isGroupComputation ? 1 : 0}${athlete.name}${amount}`,
                                    ),
                                  },
                                })) ?? []),

                                ...(soloParentIdList?.map((soloParent) => ({
                                  applyDiscount: {
                                    orderID: null,
                                    discountID: null,
                                    discountType: 'Solo Parent' as const,
                                    idNo: soloParent.id,
                                    additionalDetails: {
                                      name: soloParent.name,
                                      childName: soloParent.childName,
                                      childBirthDate: moment(
                                        soloParent.birthdate,
                                      ).format(payloadDateFormat),
                                      childAge: Number(soloParent.age),
                                    },
                                    amount,
                                    signature: hashSignature(
                                      `${propertyID}nullnullSolo Parent${soloParent.id}${isGroupComputation ? 1 : 0}${soloParent.name}${soloParent.childName}${moment(
                                        soloParent.birthdate,
                                      ).format(
                                        payloadDateFormat,
                                      )}${soloParent.age}${amount}`,
                                    ),
                                  },
                                })) ?? []),
                              ]
                            : [
                                ...(seniorIdList?.flatMap((senior) =>
                                  updatedEditItems.map((item) => ({
                                    applyDiscount: {
                                      orderID: item.orderID,
                                      discountID: null,
                                      discountType: 'Senior Citizen' as const,
                                      idNo: senior.id,
                                      additionalDetails: {
                                        name: senior.name,
                                        tin: senior?.tin ?? '',
                                      },
                                      amount,
                                      signature: hashSignature(
                                        `${propertyID}${item.orderID}nullSenior Citizen${senior.id}${isGroupComputation ? 1 : 0}${senior.name}${senior?.tin ?? ''}${amount}`,
                                      ),
                                    },
                                  })),
                                ) ?? []),

                                ...(pwdIdList?.flatMap((pwd) =>
                                  updatedEditItems.map((item) => ({
                                    applyDiscount: {
                                      orderID: item.orderID,
                                      discountID: null,
                                      discountType: 'PWD' as const,
                                      idNo: pwd.id,
                                      additionalDetails: {
                                        name: pwd.name,
                                        tin: pwd?.tin ?? '',
                                      },
                                      amount,
                                      signature: hashSignature(
                                        `${propertyID}${item.orderID}nullPWD${pwd.id}${isGroupComputation ? 1 : 0}${pwd.name}${pwd?.tin ?? ''}${amount}`,
                                      ),
                                    },
                                  })),
                                ) ?? []),

                                ...(athleteIdList?.flatMap((athlete) =>
                                  updatedEditItems.map((item) => ({
                                    applyDiscount: {
                                      orderID: item.orderID,
                                      discountID: null,
                                      discountType: 'Athlete' as const,
                                      idNo: athlete.id,
                                      additionalDetails: {
                                        name: athlete.name,
                                      },
                                      amount,
                                      signature: hashSignature(
                                        `${propertyID}${item.orderID}nullAthlete${athlete.id}${isGroupComputation ? 1 : 0}${athlete.name}${amount}`,
                                      ),
                                    },
                                  })),
                                ) ?? []),

                                ...(soloParentIdList?.flatMap((soloParent) =>
                                  updatedEditItems.map((item) => ({
                                    applyDiscount: {
                                      orderID: item.orderID,
                                      discountID: null,
                                      discountType: 'Solo Parent' as const,
                                      idNo: soloParent.id,
                                      additionalDetails: {
                                        name: soloParent.name,
                                        childName: soloParent.childName,
                                        childBirthDate: moment(
                                          soloParent.birthdate,
                                        ).format(payloadDateFormat),
                                        childAge: Number(soloParent.age),
                                      },
                                      amount,
                                      signature: hashSignature(
                                        `${propertyID}${item.orderID}nullSolo Parent${soloParent.id}${isGroupComputation ? 1 : 0}${soloParent.name}${soloParent.childName}${moment(
                                          soloParent.birthdate,
                                        ).format(
                                          payloadDateFormat,
                                        )}${soloParent.age}${amount}`,
                                      ),
                                    },
                                  })),
                                ) ?? []),
                              ];

                          await postDiscount({
                            transactionID,
                            payload: {
                              properties: [
                                {
                                  property: {
                                    propertyID,
                                    isGroupComputation,
                                    applyDiscounts,
                                    signature: hashSignature(
                                      `${propertyID}${isGroupComputation}${applyDiscounts.length}`,
                                    ),
                                  },
                                },
                              ],
                            },
                          })
                            .unwrap()
                            .then(() => {
                              toast({
                                severity: 'success',
                                message: 'Discount applied successfully',
                              });
                              discountModalDisclosure.onClose();
                              setEditItems([]);
                            })
                            .catch((error) => {
                              toast({ severity: 'warning', message: error });
                              setEditItems([]);
                              discountModalDisclosure.onClose();
                            });
                        }
                      } catch (error) {
                        toast({ severity: 'warning', message: error });
                        setEditItems([]);
                        discountModalDisclosure.onClose();
                      } finally {
                        refetch?.();
                      }
                    },
                    'issue_disc',
                    discountModalDisclosure.onClose,
                  );
                }}
                totalPax={parseInt(transactionDetails?.totalPax ?? '0') ?? 1}
                //
                // Not required on Rates Plotting
                //
                selectedIDs={''}
                useProperty={''}
                ledgerRefetch={() => {}}
                handleConfig={() => {}}
                maxAmount={totalAmount}
                disabledRegular={editItems.length <= 0}
                discountList={discountList}
                discounts={discounts}
                disabledManual={true}
                disabledPercentage={true}
                hideRemarks={true}
              />
            </ModalWrapper>
          ),
          [discountModalDisclosure.isOpen, editItems],
        )}

        {/* CHANGE ROUTE DIALOG */}
        <POSConfirmationDialog
          {...{
            dialogDisclosure,
            ...(initialSelectedTransaction !== undefined && {
              desc: 'Are you sure you want to change table? Unsaved data will be lost.',
            }),
            onConfirm: async () => {
              if (initialSelectedTransaction !== undefined) {
                setSelectedTransaction(initialSelectedTransaction);
                const selectedTransaction =
                  tableTransactions?.[initialSelectedTransaction];
                selectedTransaction &&
                  dispatch(setTransactionDetails(selectedTransaction));
                setInitialSelectedTransaction(undefined);
                dialogDisclosure.onClose();
                return;
              }
              if (blocker.status === 'blocked') {
                blocker.proceed();
                dispatch(clearPOSTableArea());
                return;
              }
            },
            onClose: () => blocker.cancel(),
          }}
        />
      </>
    </>
  );
};

export default memo(POSOrderTakingWindow);
